import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Center,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  Stack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber
} from '@chakra-ui/react';
import { IoGridOutline } from 'react-icons/io5';
import { ImStatsDots } from 'react-icons/im';
import { AiFillFormatPainter } from 'react-icons/ai';
import { RiAuctionLine } from 'react-icons/ri';
import { ItemModel } from 'models';
import { useAdminUser } from 'hooks/queries/useAdminUser';
import { useWishlist, useWishlistArtists, useWishlistListings } from 'hooks/queries/useWishlist';
import { TabGroup, EmptyState, ItemGrid, ArtistGrid, PaginatedListingGrid, UserWishlistBadge } from 'components';
import ArtistDistributionPieChart from 'components/Charts/ArtistDistributionPieChart';
import TypeDistributionPieChart from 'components/Charts/TypeDistributionPieChart';
import { NavigationService } from 'services';
import { toDollarFormat } from 'utils/format';
import { getSelectedTabFromURL } from 'utils/url';
import { QuerySearchParameters } from 'constants/url';

export default function WishlistDetailsView() {
  const { id: wishlistId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [listingPage, setListingPage] = useState(1);

  const {
    data: wishlist,
    isLoading: wishlistLoading
  } = useWishlist(wishlistId);

  const {
    data: wishlistArtists,
    isLoading: artistsLoading
  } = useWishlistArtists(wishlistId);

  const {
    data: wishlistListings,
    isLoading: listingsLoading
  } = useWishlistListings(wishlistId, listingPage);
  const { data: ownerUser, isLoading: userLoading, isError: userError } = useAdminUser(wishlist?.user);

  useEffect(() => {
    setListingPage(1);
  }, [wishlistId]);

  useEffect(() => {
    if (wishlist && wishlistArtists) {
      wishlist.setArtists(wishlistArtists);
    }
  }, [wishlist, wishlistArtists]);

  const wishlistItems = useMemo<ItemModel[]>(() => {
    if (!wishlist) {
      return [];
    }

    return wishlist.getWishlistItems();
  }, [wishlist]);

  const initialTab = getSelectedTabFromURL(location);

  const updateTabInURL = (tabIndex: number): void => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(QuerySearchParameters.TAB, tabIndex.toString());
    navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
  };

  const onTabChange = (index: number): void => {
    const currentTab = getSelectedTabFromURL(location);
    if (currentTab !== index) {
      updateTabInURL(index);
    }
  };

  const onItemCardClick = (item: ItemModel) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName()));
  };

  const onArtistCardClick = (artistId: string, artistName: string) => {
    navigate(NavigationService.getArtistDetailsPath(artistId, artistName));
  };

  const onBrowseItems = () => {
    navigate(NavigationService.getExplorePath());
  };

  const onBrowseArtists = () => {
    navigate(NavigationService.getArtistsPath());
  };

  const onListingPageSelect = (page: number) => {
    setListingPage(page);
  };

  if (!wishlistId) {
    return (
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        <EmptyState
          header="Wishlist Not Found"
          description="A wishlist identifier is required to view details."
          showButton={false}
        />
      </Box>
    );
  }

  if (wishlistLoading && !wishlist) {
    return (
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        <Center>
          <Spinner size="xl" />
        </Center>
      </Box>
    );
  }

  if (!wishlist && !wishlistLoading) {
    return (
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        <EmptyState
          header="Wishlist Not Found"
          description="The requested wishlist could not be found."
          showButton={false}
        />
      </Box>
    );
  }

  const renderWishlistItems = () => {
    if (!wishlist) {
      return null;
    }

    return (
      <Box my={{ sm: '24px', xl: '0px' }} textAlign={{ base: 'center', md: 'left' }}>
        <ItemGrid
          items={wishlistItems}
          isLoading={wishlistLoading}
          gridDisplay
          loadingRows={10}
          onItemCardClick={onItemCardClick}
          onBrowseClick={onBrowseItems}
        />
      </Box>
    );
  };

  const renderWishlistStats = () => {
    if (!wishlist) {
      return null;
    }

    if (wishlistLoading) {
      return (
        <Center>
          <Spinner />
        </Center>
      );
    }

    if (!wishlistItems.length) {
      return (
        <EmptyState
          header="No Stats"
          description="No items in wishlist to display statistics."
          showButton={false}
        />
      );
    }

    return (
      <Flex direction={{ base: 'column', md: 'row' }} gap={4} justify="center">
        <ArtistDistributionPieChart items={wishlistItems} artists={wishlistArtists || []} />
        <TypeDistributionPieChart items={wishlistItems} />
      </Flex>
    );
  };

  const renderWishlistArtists = () => {
    if (!wishlist) {
      return null;
    }

    if (artistsLoading) {
      return (
        <Center>
          <Spinner />
        </Center>
      );
    }

    if (!wishlistArtists?.length) {
      return (
        <EmptyState
          header="No Artists"
          description="No artists available for this wishlist."
          showButton={false}
        />
      );
    }

    return (
      <ArtistGrid
        artists={wishlistArtists}
        isLoading={artistsLoading}
        gridDisplay
        onArtistCardClick={(artist) => onArtistCardClick(artist.id, artist.name)}
        onBrowseClick={onBrowseArtists}
      />
    );
  };

  const renderWishlistListings = () => {
    if (!wishlist) {
      return null;
    }

    if (listingsLoading && !wishlistListings) {
      return (
        <Center>
          <Spinner />
        </Center>
      );
    }

    if (!wishlistListings || !wishlistListings.data.length) {
      return (
        <EmptyState
          header="No Listings"
          description="No listings associated with this wishlist."
          showButton={false}
        />
      );
    }

    return (
      <PaginatedListingGrid
        paginatedListings={wishlistListings}
        isLoading={listingsLoading}
        scrollMode={false}
        showAssociationAction={false}
        onPageSelect={onListingPageSelect}
      />
    );
  };

  const renderUserBanner = () => {
    if (userLoading) {
      return (
        <Box borderBottomWidth="1px" borderRadius="lg" paddingY={10}>
          <Center>
            <Spinner />
          </Center>
        </Box>
      );
    }

    if (userError || !ownerUser) {
      return (
        <EmptyState
          header="User Not Found"
          description="Unable to load user information for this wishlist."
          showButton={false}
        />
      );
    }

    return (
      <UserWishlistBadge
        user={ownerUser}
        wishlist={wishlist}
        dataLoading={wishlistLoading}
        userLoading={userLoading}
      />
    );
  };

  const tabLabels = [
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="wishlist-items-tab">
      <Icon as={IoGridOutline} />
      <Text>Items</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="wishlist-stats-tab">
      <Icon as={ImStatsDots} />
      <Text>Stats</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="wishlist-artists-tab">
      <Icon as={AiFillFormatPainter} />
      <Text>Artists</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="wishlist-sales-tab">
      <Icon as={RiAuctionLine} />
      <Text>Sales</Text>
    </Flex>
  ];

  const tabContent = [
    renderWishlistItems(),
    renderWishlistStats(),
    renderWishlistArtists(),
    renderWishlistListings()
  ];

  const wishlistValue = toDollarFormat(wishlist.getEstimatedMarketValue());
  const wishlistItemCount = wishlistItems.length;
  const wishlistArtistCount = wishlist.getArtistIds()?.length || 0;

  return (
    <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
      <Box mb={6}>
        {renderUserBanner()}
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={8}>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Total Items</StatLabel>
          <StatNumber>{wishlistItemCount}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Unique Artists</StatLabel>
          <StatNumber>{wishlistArtistCount}</StatNumber>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4} bg="whiteAlpha.50">
          <StatLabel>Estimated Value</StatLabel>
          <StatNumber>{wishlistValue}</StatNumber>
        </Stat>
      </SimpleGrid>

      <Stack spacing={6}>
        <Box>
          <Heading fontSize="lg" mb={4}>
            Wishlist Overview
          </Heading>
          <TabGroup
            labels={tabLabels}
            content={tabContent}
            startingTab={initialTab}
            onChange={onTabChange}
          />
        </Box>
      </Stack>
    </Box>
  );
}
